import { Skeleton } from '@commander/ui';
import type { KPIBlock, KPIMetric } from '../blocks/kpi.js';
import { formatCompactValue } from './utils.js';

// --- Trend Arrow ---

function TrendArrow({ direction }: { direction: 'up' | 'down' | 'flat' }) {
  if (direction === 'flat') {
    return (
      <svg
        className="h-3 w-3"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="M5 12h14" />
      </svg>
    );
  }
  return (
    <svg
      className="h-3 w-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      {direction === 'up' ? (
        <path d="M7 17l5-5 5 5M7 7l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M7 7l5 5 5-5M7 17l5-5 5-5" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

// --- Sentiment Color ---

function getSentimentClasses(
  sentiment?: 'positive' | 'negative' | 'neutral',
  direction?: 'up' | 'down' | 'flat'
): string {
  const effective =
    sentiment ?? (direction === 'up' ? 'positive' : direction === 'down' ? 'negative' : 'neutral');
  switch (effective) {
    case 'positive':
      return 'text-green-600 dark:text-green-400';
    case 'negative':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-muted-foreground';
  }
}

// --- Single Metric Card ---

function MetricCard({ metric }: { metric: KPIMetric }) {
  const safeValue = Number.isFinite(metric.value) ? metric.value : 0;
  const formattedValue = formatCompactValue(safeValue, metric.format);

  return (
    <div className="rounded-lg border bg-card p-3 text-card-foreground">
      <p className="text-xs text-muted-foreground">{metric.label}</p>
      <p className="mt-1 text-lg font-semibold leading-none">{formattedValue}</p>
      {metric.trend && (
        <div
          className={`mt-1.5 inline-flex items-center gap-0.5 ${getSentimentClasses(metric.trend.sentiment, metric.trend.direction)}`}
        >
          <TrendArrow direction={metric.trend.direction} />
          <span className="text-xs font-medium">
            {metric.trend.value > 0 ? '+' : ''}
            {metric.trend.value}%
          </span>
          {metric.trend.label && (
            <span className="ml-0.5 text-xs text-muted-foreground">{metric.trend.label}</span>
          )}
        </div>
      )}
    </div>
  );
}

// --- Main KPICards Component ---

export function KPICards({ data }: { data: KPIBlock }) {
  const gridCols = data.metrics.length === 1 ? 'grid-cols-1' : 'grid-cols-2';

  return (
    <section aria-label="Key metrics" className={`grid ${gridCols} gap-2`}>
      {data.metrics.map((metric) => (
        <MetricCard key={metric.label} metric={metric} />
      ))}
    </section>
  );
}

// --- Skeleton ---

export function KPISkeleton() {
  return (
    <div className="grid grid-cols-2 gap-2">
      {['a', 'b'].map((id) => (
        <div key={id} className="rounded-lg border bg-card p-3">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="mt-2 h-5 w-20" />
          <Skeleton className="mt-2 h-3 w-12" />
        </div>
      ))}
    </div>
  );
}
